/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import AdditionalFilterDialog from 'bundles/enterprise-admin-user-filters/components/AdditionalFilterDialog';
import AdditionalFilterSubSection from 'bundles/enterprise-admin-user-filters/components/AdditionalFilterSubSection';
import ContractSelectionDrawer from 'bundles/enterprise-admin-user-filters/components/ContractSelectionDrawer';
import { Section } from 'bundles/enterprise-admin-user-filters/components/ui';
import { getAdditionalFilterOptions } from 'bundles/enterprise-admin-user-filters/constants';
import type {
  AdditionalAttributeFilter,
  AdditionalFilterType,
  EnrichedContract,
  EnrichedData,
} from 'bundles/enterprise-admin-user-filters/types';
import { getContractFilterOptions, normalizeContractId } from 'bundles/enterprise-admin-user-filters/utils';
import { Spacer, useModalState } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-user-filters';

const getLabel = (additionalFilter: AdditionalFilterType) => {
  if (additionalFilter === 'CONTRACT') {
    return _t('Active contract');
  } else if (additionalFilter === 'BUSINESS_UNIT') {
    return _t('Business unit');
  } else if (additionalFilter === 'JOB_TITLE') {
    return _t('Job title');
  } else if (additionalFilter === 'JOB_TYPE') {
    return _t('Job type');
  } else if (additionalFilter === 'LOCATION') {
    return _t('Location');
  } else if (additionalFilter === 'MANAGER') {
    return _t('Manager');
  } else {
    throw Error(`Unexpected filter: ${additionalFilter}`);
  }
};

const AdditionalFilterSection = ({
  additionalFilters,
  thirdPartyOrganization,
  enrichedData,
  onChange,
  onEnrichedContractsChange,
}: {
  additionalFilters: AdditionalAttributeFilter[];
  thirdPartyOrganization: { id: string; slug: string };
  enrichedData: EnrichedData;
  onEnrichedContractsChange: (contracts: EnrichedContract[]) => void;
  onChange: (value: AdditionalAttributeFilter[]) => void;
}) => {
  const [activityFilterDialogOpen, openActivityFilterDialog, closeActivityFilterDialog] = useModalState();
  const [contractSelectionDrawerOpen, openContractSelectionDrawer, closeContractSelectionDrawer] = useModalState();

  return (
    <React.Fragment>
      {additionalFilters.length > 0 && (
        <Section
          css={{
            marginBottom: 'var(--cds-spacing-150)',
          }}
        >
          <Typography2 component="h3" variant="subtitleMedium">
            {_t('Additional filters')}
          </Typography2>
          {additionalFilters.map((additionalFilter) => {
            const label = getLabel(additionalFilter.attributeName);
            return (
              <AdditionalFilterSubSection
                key={additionalFilter.attributeName}
                filter={additionalFilter.attributeName}
                values={additionalFilter.values}
                label={label}
                enrichedData={enrichedData}
                onSelect={additionalFilter.attributeName === 'CONTRACT' ? openContractSelectionDrawer : undefined}
                onAdd={
                  additionalFilter.attributeName !== 'CONTRACT'
                    ? (value: string) => {
                        onChange(
                          additionalFilters.map((_) =>
                            _ === additionalFilter
                              ? {
                                  ...additionalFilter,
                                  values: additionalFilter.values.concat(value),
                                }
                              : _
                          )
                        );
                      }
                    : undefined
                }
                onRemoveFilter={() => {
                  onChange(additionalFilters.filter((_) => _.attributeName !== additionalFilter.attributeName));
                }}
                onDismissValue={(value) => {
                  const valueToUse = value.toLowerCase().includes('contract~') ? value.split('~')[1] : value;
                  onChange(
                    additionalFilters.map((filter) =>
                      filter.attributeName === additionalFilter.attributeName
                        ? {
                            ...filter,
                            values: filter.values.filter((_) => _ !== valueToUse),
                          }
                        : filter
                    )
                  );
                }}
                onDismissAllValues={() => {
                  onChange(
                    additionalFilters.map((filter) =>
                      filter.attributeName === additionalFilter.attributeName
                        ? {
                            ...filter,
                            values: [],
                          }
                        : filter
                    )
                  );
                }}
              />
            );
          })}
        </Section>
      )}
      {additionalFilters.length < getAdditionalFilterOptions().length + 1 && (
        <Section isZeroState>
          <Typography2 component="h3">
            {_t(
              'Filter learners by additional attributes, such as: active contracts, job title, business unit, active contracts, and more'
            )}
          </Typography2>
          <Spacer size={12} />
          <Button
            css={css`
              ${breakpoints.down('xs')} {
                width: 100%;
                max-width: none;
              }
            `}
            variant="secondary"
            size="small"
            icon={<AddIcon size="small" />}
            iconPosition="before"
            onClick={openActivityFilterDialog}
            data-testid="add-additional-filters"
          >
            {_t('Add additional filters')}
          </Button>
        </Section>
      )}
      {activityFilterDialogOpen && (
        <AdditionalFilterDialog
          open
          otherFilters={additionalFilters}
          onClose={closeActivityFilterDialog}
          onSelect={(additionalFilter: AdditionalFilterType) => {
            onChange([
              ...additionalFilters,
              {
                attributeName: additionalFilter,
                values: [],
              },
            ]);
            closeActivityFilterDialog();
            if (additionalFilter === 'CONTRACT') {
              openContractSelectionDrawer();
            }
          }}
        />
      )}
      {contractSelectionDrawerOpen && (
        <ContractSelectionDrawer
          thirdPartyOrganization={thirdPartyOrganization}
          isOpen
          selectedContracts={getContractFilterOptions(enrichedData, additionalFilters)}
          onClose={closeContractSelectionDrawer}
          onApply={(selectedContracts) => {
            onChange(
              additionalFilters.map((filter) =>
                filter.attributeName === 'CONTRACT'
                  ? {
                      ...filter,
                      values: selectedContracts.map((value) => normalizeContractId(value.id)),
                    }
                  : filter
              )
            );
            onEnrichedContractsChange(
              selectedContracts.map((_) => ({ contractId: normalizeContractId(_.id), contractName: _.label }))
            );
          }}
        />
      )}
    </React.Fragment>
  );
};
export default AdditionalFilterSection;
